import { useState } from "react";
import { CalendarDays } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import {
  formatDisplayDate,
  parseIsoDate,
  startOfMonth,
  toIsoDate,
} from "./date-calendar";
import {
  DateCalendarGrid,
  initialCalendarMonth,
} from "./date-calendar-grid";

export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "default";
  allowClear?: boolean;
  id?: string;
}

/** Themed popover calendar. Value is `YYYY-MM-DD` (same as a native date input). */
export function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  disabled = false,
  className,
  size = "default",
  allowClear = false,
  id,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseIsoDate(value);
  const [month, setMonth] = useState(() =>
    initialCalendarMonth(value),
  );

  const todayIso = toIsoDate(new Date());

  function openChange(next: boolean) {
    setOpen(next);
    if (next) setMonth(startOfMonth(selected ?? new Date()));
  }

  function pick(iso: string) {
    onChange(iso);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "flex w-full items-center gap-2 rounded-md border border-input bg-transparent text-left shadow-sm transition-colors",
            "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            size === "sm" ? "h-8 px-2 text-xs" : "h-9 px-3 text-sm",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <CalendarDays
            className={cn(
              "shrink-0 text-muted-foreground",
              size === "sm" ? "size-3.5" : "size-4",
            )}
          />
          <span className="min-w-0 flex-1 truncate">
            {value ? formatDisplayDate(value) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[17.5rem] p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DateCalendarGrid
          month={month}
          onMonthChange={setMonth}
          selected={value}
          onDayClick={pick}
        />

        <div className="mt-2 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => pick(todayIso)}
          >
            Today
          </Button>
          {allowClear && value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
